import React, { memo } from 'react'
import { MidDot } from 'components/atoms/icons'
import moment from 'moment'
import { InlineMessage } from 'transfix-ui/components/InlineMessage/InlineMessage'
import { INextStop, IDriverLocation, IUserProfileDetails } from 'types/Location'
import {
  appMessaging,
  locationMessaging,
  drivingMessaging,
  pushNotificationsMessaging,
  messagingTypes,
  NEVER_SIGNED_IN,
} from 'apps/user/constants/driverLocations'
import { atLocation } from 'utils/calculateDistance'
import { formatActivityDateTime } from 'utils/formatDatetime'
import { getTrackingProperties } from 'utils/trackingHelpers'

interface PhoneDetailsProps {
  userProfileDetails: IUserProfileDetails
  driverLocations: IDriverLocation[]
  nextStop?: Pick<INextStop, 'latitude' | 'longitude'>
  shipmentStatus: string
}

export const PhoneDetails = memo<PhoneDetailsProps>(
  ({ driverLocations, nextStop, shipmentStatus, userProfileDetails }) => {
    const {
      lastMobileHeartbeat,
      lastSignInAt,
      locationEnable,
      mobileAppNotificationToken,
      phoneModel,
      phoneOs,
      transfixVersion,
    } = userProfileDetails || {}
    const MANUAL_SOURCE = 'Manual'
    const [lastLocation] = (driverLocations || []).filter(l => l.sourceType !== MANUAL_SOURCE) || []
    const { phoneTimestamp, speed, timezone } = lastLocation || {}
    const { isTracking } = getTrackingProperties(phoneTimestamp, timezone)
    const isDriving: boolean = isTracking && Number(speed) > 5
    const atFacility: boolean = atLocation(lastLocation || {}, nextStop, shipmentStatus)
    const formattedSignIn: string = formatActivityDateTime(lastSignInAt)
    const lastHeartbeat: string = formatActivityDateTime(lastMobileHeartbeat)
    const halfAnHourAgo = moment().subtract(30, 'minutes').toDate()
    let sourceApp: string = 'Transfix App'

    const messaging: messagingTypes[] = [
      locationMessaging(locationEnable),
      appMessaging(moment(lastMobileHeartbeat).toDate() > halfAnHourAgo),
      drivingMessaging(isDriving, atFacility),
      pushNotificationsMessaging(!!mobileAppNotificationToken),
    ]
    if (lastLocation && lastLocation.sourceType?.match(/fms/i)) {
      sourceApp = 'FMS Mobile'
    }
    return (
      <>
        <p className='text-secondary'>
          {transfixVersion && (
            <>
              {sourceApp} v{transfixVersion}
            </>
          )}
          {phoneModel && (
            <>
              <MidDot />
              {phoneModel}
            </>
          )}
          {phoneOs && (
            <>
              <MidDot />
              {phoneOs}
            </>
          )}
        </p>
        {messaging.map(message => (
          <div className='mb2' key={`${message.label}-${message.type}`}>
            <InlineMessage message={message.label} type={message.type} />
          </div>
        ))}
        <p>
          <strong>Last signed in:</strong> {lastSignInAt ? formattedSignIn : NEVER_SIGNED_IN}
          <br />
          <strong>Last mobile activity:</strong> {lastHeartbeat}
        </p>
      </>
    )
  }
)
